import React from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import PropTypes from 'prop-types';
import { B2CDomainSelector, utils } from '@yola/webapp-plugins';
import { i18next, site } from '@yola/ws-sdk';
import {
  constants as subscriptionManagerConstants,
  selectors as subscriptionSelectors,
  verifiers as subscriptionManagerVerifiers,
} from '@yola/subscription-manager-js';
import segment from 'src/js/modules/analytics/segment';
import common from 'src/js/modules/common';
import getPublishUrl from 'src/js/modules/editor/helpers/get-publish-url';
import config from 'src/js/modules/config';
import dialogs from 'yola-editor/src/js/modules/dialogs';
import actions from 'yola-editor/src/js/modules/upsell/actions';
import features from 'yola-editor/src/js/modules/features';
import beforeUnloadListener from 'yola-editor/src/js/utils/before-unload-listener';
import getUserPreferences from 'yola-editor/src/js/modules/user/selectors/user-preferences';
import getSiteId from 'yola-editor/src/js/modules/auth/selectors/site-id';
import getSiteTemplateSlug from 'yola-editor/src/js/modules/site/selectors/site-template-slug';
import { redirectToUrl } from 'yola-editor/src/js/router/utils/redirect-to-url';
import useUpsellSettings from 'yola-editor/src/js/modules/upsell/hooks/use-upsell-settings';
import getSiteData from 'yola-editor/src/js/modules/site/selectors/site-data';
import convertStringToDomainName from 'yola-editor/src/js/modules/upsell/helpers/convert-string-to-domain-name';
import useDomainsExtraPrice, {
  extraPrices,
} from 'yola-editor/src/js/modules/upsell/hooks/use-domains-extra-price';
import getPurchaseFlowType from 'yola-editor/src/js/modules/upsell/helpers/get-purchase-flow-type';
import {
  CUSTOM_DOMAIN,
  PUBLISHING,
} from 'yola-editor/src/js/modules/upsell/constants/upsell-types';
import publishing from 'yola-editor/src/js/modules/publishing';
import useFeatureFlags from 'yola-editor/src/js/modules/feature-flags/hooks/use-feature-flags';
import { saveSiteWithEffect } from 'src/js/modules/saving/helpers/save-site';
import actionIds from '../constants/domain-purchase-action-ids';
import usePurchasedDomainsData from '../hooks/use-purchased-domains-data';
import useSpecialOffer from '../hooks/use-special-offer';

const {
  SubscriptionType: { DOMAIN_SUBSCRIPTION },
  SubscriptionTerm: { ANNUAL, MONTHLY },
} = subscriptionManagerConstants;

const defaultCaptionOverrides = {};

function B2cDomainPurchaseDialogContainer({
  triggerId,
  subscriptionType = '',
  domainNameSuggestion = '',
  withFreeSubdomain = false,
  withPurchasedDomains = false,
  shouldPublishOnSuccess = false,
  captionsOverrides = defaultCaptionOverrides,
  premiumFeature = '',
  onClose = Function.prototype,
}) {
  const { constants, track, trackAsync } = segment;
  const dispatch = useDispatch();
  const { currency } = useSelector(getUserPreferences);
  const siteId = useSelector(getSiteId);
  const templateBuildSlug = useSelector(getSiteTemplateSlug);
  const purchaseFlowType = useSelector(getPurchaseFlowType);
  const siteData = useSelector(getSiteData, shallowEqual);
  const { name: siteName } = siteData;
  const siteSettings = useSelector(site.selectors.getSettings);
  const packageType = useSelector(subscriptionSelectors.getCurrentPackageType);
  const activeHostingSubscription = useSelector(subscriptionSelectors.getActiveHostingSubscription);
  const activeSubscriptions = useSelector(subscriptionSelectors.getActiveSubscriptions);
  const myYolaConfig = useSelector(config.selectors.getMyYolaConfig);
  const limits = features.accessors.getCurrentPackageFeatureLimits();

  const [featureFlags] = useFeatureFlags(['upgrade_to_publish']);
  const extraPriceType = useDomainsExtraPrice();
  const upsellSettings = useUpsellSettings({
    isFreeDomainAvailable: extraPrices.NO_DISCOUNTS === extraPriceType,
  });
  const purchasedDomainsData = usePurchasedDomainsData(withPurchasedDomains);

  const { upgrade_to_publish: isUpgradeToPublishEnabled } = featureFlags;
  const { businessName } = siteSettings;
  const customDomainFeatureAvailable = limits.customDomain.available;
  const isSubscriptionExpired =
    subscriptionManagerVerifiers.hasExpiredPaidSubscription(activeSubscriptions);
  const isPublishDomainAvailable = customDomainFeatureAvailable || isSubscriptionExpired;

  const trackingData = {
    siteId,
    templateBuildSlug,
  };

  const trackUpgradeInitiated = () =>
    trackAsync(constants.events.UPGRADE_INITIATED, {
      ...trackingData,
      premiumFeature: premiumFeature || CUSTOM_DOMAIN,
      purchaseFlowType,
      productCategory: constants.common.EDITOR_PRODUCT_CATEGORY,
    });

  const onSpecialOfferSwitch = (enabled) => {
    track(constants.events.DOMAIN_PURCHASE_SPECIAL_OFFER_SWITCHED, {
      ...trackingData,
      enabled,
    });
  };

  const showB2cDomainPurchaseDialog = () =>
    dispatch(
      dialogs.actions.show(dialogs.dialogTypes.B2C_DOMAIN_PURCHASE, {
        triggerId,
        subscriptionType,
        domainNameSuggestion,
        withFreeSubdomain,
        withPurchasedDomains,
        shouldPublishOnSuccess,
        captionsOverrides,
        premiumFeature,
        onClose,
      })
    );

  const trackDomainPurchaseClicked = ({ domain, searchString, actionId }) => {
    const indexOfDomainTypeSeparator = domain.indexOf('.');
    const domainNameSelected = domain.substring(0, indexOfDomainTypeSeparator);
    const domainTypeSelected = domain.substring(indexOfDomainTypeSeparator + 1);
    track(constants.events.DOMAIN_PURCHASE_CLICKED, {
      ...trackingData,
      triggerId,
      domainName: searchString,
      domainNameSelected,
      domainTypeSelected,
      actionId,
      activeHostingSubscription: packageType,
    });
  };

  const redirectToPaymentPage = (domain, successUrl) => {
    beforeUnloadListener.remove();

    dispatch(
      dialogs.actions.show(dialogs.dialogTypes.PENDING, {
        captions: {
          title: i18next.t('Loading...'),
        },
      })
    );

    const paymentParams = {
      products: [subscriptionType, domain && DOMAIN_SUBSCRIPTION].filter(Boolean),
      term: ANNUAL,
      latitude_site_id: siteId,
      ...(domain && { domain_name: domain }),
      ...(activeHostingSubscription?.term === MONTHLY && { p1y_upgrade: true }),
      ...(successUrl && { success_url: successUrl }),
    };

    const paymentUrl = utils.generatePaymentUrl(paymentParams);

    if (subscriptionType) {
      trackUpgradeInitiated().then(() => {
        saveSiteWithEffect(() => {
          redirectToUrl(paymentUrl);
        });
      });
      return;
    }

    saveSiteWithEffect(() => {
      redirectToUrl(paymentUrl);
    });
  };

  const showPaywall = ({ domain, ...restProps }) =>
    upsellSettings[premiumFeature === PUBLISHING ? 'publishing' : 'customDomain']
      .onUpgrade({
        ...(domain && { domainName: domain }),
        ...restProps,
      })
      .then(Function.prototype, showB2cDomainPurchaseDialog);

  const processPublishDomain = ({ domain, successUrl }) => {
    if (isSubscriptionExpired) {
      dispatch(
        dialogs.actions.show(dialogs.dialogTypes.SUBSCRIPTION_EXPIRED, {
          onClose: () => dispatch(publishing.actions.publishWebsite(domain)),
          additionalPaymentParams: {
            successUrl,
          },
        })
      );
      return;
    }

    dispatch(publishing.actions.publishWebsite(domain));
  };

  const onSelect = ({ domain, searchString, shouldBundleSubscription }) => {
    trackDomainPurchaseClicked({ domain, searchString, actionId: actionIds.BUY_DOMAIN });

    const successUrl = shouldPublishOnSuccess ? getPublishUrl(domain) : null;
    const isPurchaseDomainAvailable =
      customDomainFeatureAvailable || isSubscriptionExpired || subscriptionType;

    if (isPurchaseDomainAvailable && !shouldBundleSubscription) {
      redirectToPaymentPage(domain, successUrl);
      return;
    }

    showPaywall({ domain, successUrl });
  };

  const onFreeSubdomainSelect = ({ searchString }) => {
    const domain = publishing.constants.FREE_YOLA_SUBDOMAIN;
    const successUrl = getPublishUrl(domain);

    trackDomainPurchaseClicked({
      domain,
      searchString,
      actionId: actionIds.GET_FREE_DOMAIN,
    });

    if (isUpgradeToPublishEnabled) {
      if (isPublishDomainAvailable) {
        processPublishDomain({ domain, successUrl });
      } else {
        showPaywall({ successUrl });
      }

      return;
    }

    dispatch(publishing.actions.publishWebsite());
  };

  const onPurchasedDomainSelect = (domain) => {
    const successUrl = getPublishUrl(domain);

    track(constants.events.DOMAIN_POINTING_CLICKED, {
      ...trackingData,
      domainNameSelected: domain,
    });

    if (isPublishDomainAvailable) {
      processPublishDomain({ domain, successUrl });
      return;
    }

    showPaywall({ successUrl });
  };

  const onConnectThirdPartyDomainClick = (domain) => {
    const myYolaDomainsUrl = myYolaConfig.domains_url;

    const searchParams = new URLSearchParams({
      third_party_domain: domain,
      site_id: siteId,
    });

    const domainsUrl = `${myYolaDomainsUrl}?${searchParams.toString()}`;

    if (isPublishDomainAvailable) {
      dispatch(
        dialogs.actions.show(dialogs.dialogTypes.PENDING, {
          captions: {
            title: i18next.t('Loading...'),
          },
        })
      );

      redirectToUrl(domainsUrl);
      return;
    }

    showPaywall({ successUrl: domainsUrl, isConnectThirdPartyDomain: true });
  };

  const onTabChange = (tabId) => {
    track(constants.events.DOMAIN_SELECTOR_TABS_TRIGGER_CLICKED, {
      ...trackingData,
      triggerId: tabId,
    });
  };

  const handleClose = () => {
    track(constants.events.DOMAIN_PURCHASE_DIALOG_CANCELED, {
      ...trackingData,
      triggerId,
    });

    dispatch(dialogs.actions.hide());
    onClose();
  };

  let onNext = null;

  if (subscriptionType) {
    onNext = () => {
      track(constants.events.DOMAIN_PURCHASE_NOT_NOW_CLICKED, {
        ...trackingData,
        triggerId,
      });

      redirectToPaymentPage();
    };
  }

  const onDomainSearchFailure = () => {
    dispatch(actions.getDomainListFailure());

    track(constants.events.DOMAIN_SEARCH_FAILED, {
      ...trackingData,
      triggerId,
    });
  };

  const onSearchStart = ({ searchString, domainsRequestOptions }) => {
    if (domainsRequestOptions && domainsRequestOptions.userAction) {
      track(constants.events.DOMAIN_PURCHASE_SEARCH_CLICKED, {
        ...trackingData,
        triggerId,
        domainName: searchString,
      });
    }
  };

  const onDomainFieldChange = (option) => {
    if (option && option.tld) {
      track(constants.events.DOMAIN_PURCHASE_AUTOCOMPLETE_DOMAIN_SELECTED, {
        ...trackingData,
        triggerId,
        domainName: option.value,
        tld: option.tld,
      });
    }
  };

  const onSearchPerformed = ({ searchString, searchResults, domainsRequestOptions }) => {
    const availableDomains = searchResults.filter(({ available }) => available);
    const isSearchedDomainAvailable = availableDomains.some(({ domain }) => {
      const domainName = domain.substring(0, domain.indexOf('.'));
      return searchString === domain || searchString === domainName;
    });
    const suggestedDomainsAvailable = availableDomains.length;
    const suggestedDomainsTaken = searchResults.length - availableDomains.length;

    track(constants.events.DOMAIN_PURCHASE_SEARCH_PERFORMED, {
      ...trackingData,
      triggerId,
      domainName: searchString,
      isSearchedDomainAvailable,
      suggestedDomainsAvailable,
      suggestedDomainsTaken,
      searchClicked: Boolean(domainsRequestOptions && domainsRequestOptions.userAction),
    });
  };

  const specialOffer = useSpecialOffer({
    bundledSubscriptionType: subscriptionType,
    extraPriceType,
    activeHostingSubscription,
  });

  return (
    <B2CDomainSelector
      currency={currency}
      appElementSelector={common.constants.common.APP_ELEMENT_SELECTOR}
      onSelect={onSelect}
      onClose={handleClose}
      onNext={onNext}
      onDomainFieldChange={onDomainFieldChange}
      domainName={domainNameSuggestion || convertStringToDomainName(businessName || siteName)}
      onDomainSearchFailure={onDomainSearchFailure}
      onSearchStart={onSearchStart}
      onSearchPerformed={onSearchPerformed}
      siteId={siteId}
      withFreeSubdomain={withFreeSubdomain}
      onFreeSubdomainSelect={onFreeSubdomainSelect}
      purchasedDomainsData={purchasedDomainsData}
      onSpecialOfferSwitch={onSpecialOfferSwitch}
      captionsOverrides={captionsOverrides}
      specialOffer={specialOffer}
      activeSubscriptionTerm={activeHostingSubscription?.term}
      isUpgradeToPublishEnabled={isUpgradeToPublishEnabled}
      extraPrice={extraPriceType}
      onPurchasedDomainSelect={onPurchasedDomainSelect}
      onTabChange={onTabChange}
      onConnectThirdPartyDomainClick={onConnectThirdPartyDomainClick}
    />
  );
}

B2cDomainPurchaseDialogContainer.propTypes = {
  triggerId: PropTypes.string.isRequired,
  subscriptionType: PropTypes.string,
  domainNameSuggestion: PropTypes.string,
  withFreeSubdomain: PropTypes.bool,
  withPurchasedDomains: PropTypes.bool,
  shouldPublishOnSuccess: PropTypes.bool,
  captionsOverrides: PropTypes.shape({ closeButton: PropTypes.string }),
  premiumFeature: PropTypes.string,
  onClose: PropTypes.func,
};

export default B2cDomainPurchaseDialogContainer;
